import Vue from 'vue'
import Router from 'vue-router'
import Login from '@/views/user-auth/Login.vue'
import Layout from '@/components/Layout.vue'
import Home from '@/views/home/Home.vue'
import store from '@/store'

Vue.use(Router)

// 静态路由 - 不需要权限的路由
const constantRoutes = [
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: {
      title: '登录',
      requiresAuth: false
    }
  },
  // 404 重定向
  {
    path: '*',
    redirect: '/home'
  }
]

// 基础路由 - 需要登录但不需要特殊权限的路由
const baseRoutes = [
  {
    path: '/',
    name: 'Layout',
    component: Layout,
    redirect: '/home',
    meta: {
      requiresAuth: true
    },
    children: [
      {
        path: 'home',
        name: 'Home',
        component: Home,
        meta: {
          title: '首页',
          requiresAuth: true
        }
      },
      {
        path: 'devices',
        name: 'Devices',
        component: () => import('@/views/device-management/Devices.vue'),
        meta: { title: '设备管理', requiresAuth: true }
      },
      {
        path: 'users',
        name: 'Users',
        component: () => import('@/views/Users/Users.vue'),
        meta: { title: '用户管理', requiresAuth: true }
      },
      {
        path: 'users/list',
        name: 'UsersList',
        component: () => import('@/views/Users/List.vue'),
        meta: { title: '用户列表', requiresAuth: true }
      },
      {
        path: 'finance/withdraw-records',
        name: 'WithdrawRecords',
        component: () => import('@/views/finance-cooperation/WithdrawRecords.vue'),
        meta: { title: '用户提现管理', requiresAuth: true }
      },
      {
        path: 'system-apps',
        name: 'SystemApps',
        component: () => import('@/views/settings/SystemApps.vue'),
        meta: { title: '系统应用', requiresAuth: true }
      }
      ,
      {
        path: 'cleaning-groups',
        name: 'CleaningGroups',
        component: () => import('@/views/operation-management/CleaningGroups.vue'),
        meta: { title: '分组管理', requiresAuth: true }
      }
      ,
      {
        path: 'feedbacks',
        name: 'Feedbacks',
        component: () => import('@/views/operation-management/Feedbacks.vue'),
        meta: { title: '意见反馈', requiresAuth: true }
      }
      ,
      {
        path: 'asset-cleaning-stats',
        name: 'AssetCleaningStats',
        component: () => import('@/views/statistics/CleaningStats.vue'),
        meta: { title: '清运量统计', requiresAuth: true }
      }
      ,
      {
        path: 'cleaner-stats',
        name: 'CleanerStats',
        component: () => import('@/views/statistics/CleanerStats.vue'),
        meta: { title: '清运员统计', requiresAuth: true }
      }
      ,
      {
        path: 'asset-device-delivery-stats',
        name: 'AssetDeviceDeliveryStats',
        component: () => import('@/views/statistics/DeviceDeliveryStats.vue'),
        meta: { title: '设备投递量统计', requiresAuth: true }
      },
      {
        path: 'deliveries',
        name: 'Deliveries',
        component: () => import('@/views/operation-management/Delivery.vue'),
        meta: { title: '投递订单', requiresAuth: true }
      },
      {
        path: 'clearance',
        name: 'Clearance',
        component: () => import('@/views/operation-management/Clearance.vue'),
        meta: { title: '清运管理', requiresAuth: true }
      },
      {
        path: 'door-recycling',
        name: 'DoorRecycling',
        component: () => import('@/views/apps/DoorRecycling.vue'),
        meta: { title: '上门回收', requiresAuth: true }
      },
      {
        path: 'service-station',
        name: 'ServiceStation',
        component: () => import('@/views/apps/ServiceStation.vue'),
        meta: { title: '便民驿站', requiresAuth: true }
      },
      {
        path: 'sorting-center',
        name: 'SortingCenter',
        component: () => import('@/views/apps/SortingCenter.vue'),
        meta: { title: '分拣中心', requiresAuth: true }
      }
    ]
  }
]

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [...constantRoutes, ...baseRoutes]
})

// 路由白名单
const whiteList = ['/login']

// 路由拦截器
router.beforeEach(async (to, from, next) => {
  // 设置页面标题
  document.title = to.meta.title || '管理系统'
  
  // 获取token
  const token = localStorage.getItem('access_token')
  
  // 如果没有token，重定向到登录页
  if (!token) {
    if (whiteList.includes(to.path)) {
      next()
    } else {
      next('/login')
    }
    return
  }
  
  // 如果已登录且访问的是登录页，重定向到首页
  if (to.path === '/login') {
    next('/home')
    return
  }
  
  // 检查是否已经获取了权限信息
  const isPermissionLoaded = store.state.permission.isLoaded
  
  if (isPermissionLoaded) {
    // 如果已经获取了权限，直接进入
    next()
  } else {
    try {
      // 生成动态路由
      await store.dispatch('permission/generateRoutes')
      
      // 动态添加路由
      const asyncRoutes = store.state.permission.routes
      
      // 找到Layout路由
      const layoutRoute = router.options.routes.find(route => route.path === '/')
      
      if (layoutRoute) {
        // 将动态路由作为Layout的子路由添加
        asyncRoutes.forEach(route => {
          // 确保路由path格式正确
          let routePath = route.path
          if (routePath.startsWith('/')) {
            routePath = routePath.substring(1)
          }
          
          // 创建新的子路由对象
          const childRoute = {
            path: routePath,
            name: route.name,
            component: route.component,
            meta: route.meta,
            children: route.children || []
          }
          
          // 添加到Layout路由的children中
          if (!layoutRoute.children) {
            layoutRoute.children = []
          }
          layoutRoute.children.push(childRoute)
          
          // 同时添加到router实例中
          router.addRoute('Layout', childRoute)
        })
      }
      
      // 检查是否有需要恢复的路由
      const lastVisitedRoute = localStorage.getItem('last_visited_route')
      const token = localStorage.getItem('access_token')
      
      // 如果有需要恢复的路由、用户已登录、且恢复的路由不是登录页或首页，则优先恢复该路由
      if (lastVisitedRoute && token && lastVisitedRoute !== '/login' && lastVisitedRoute !== '/home') {
        // 使用replace避免导航冲突
        next({ path: lastVisitedRoute, replace: true })
      } else {
        // 否则按照原来的路由进入
        next({ ...to, replace: true })
      }
    } catch (error) {
      // 获取权限失败，清除token并跳转到登录页
      console.error('获取权限失败:', error)
      await store.dispatch('auth/logout')
      next('/login')
    }
  }
})

// 全局路由错误处理，用于处理重复导航错误
const originalPush = Router.prototype.push
Router.prototype.push = function push(location) {
  return originalPush.call(this, location).catch(err => {
    // 只忽略重复导航的错误，其他错误仍然抛出
    if (err.name !== 'NavigationDuplicated') {
      throw err
    }
  })
}

export default router
